import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { HeartThinIcon } from 'xyz-icon-set-react/dist/icons/HeartThinIcon';
import { useSystemTheme } from '@/ui/hooks/useSystemTheme';

interface HeartRateIndicatorProps extends PropsWithChildren {
  heartRate?: number;
  color?: string;
  size?: number;
  fillOpacity?: number;
}

const getHeartBeatScale = (timestamp: number, bpm: number) => {
  const timeInSeconds = timestamp / 1000;
  const beatInterval = 60 / bpm;
  const timeInBeatCycle = timeInSeconds % beatInterval;

  const magnitude = 0.15;
  const maxScale = 1 + magnitude;
  const restScale = 1;
  const reboundScale = restScale - magnitude;

  // Match ECG timing: QRS complex (the beat) happens at 10-15% of cycle
  if (timeInBeatCycle < 0.1 * beatInterval) {
    return restScale;
  } else if (timeInBeatCycle < 0.15 * beatInterval) {
    const progress =
      (timeInBeatCycle - 0.1 * beatInterval) / (0.05 * beatInterval);
    if (progress < 0.5) {
      return restScale + (maxScale - restScale) * (progress * 2);
    } else {
      return maxScale - (maxScale - reboundScale) * ((progress - 0.5) * 2);
    }
  } else if (timeInBeatCycle < 0.25 * beatInterval) {
    const progress =
      (timeInBeatCycle - 0.15 * beatInterval) / (0.1 * beatInterval);
    return reboundScale + (restScale - reboundScale) * progress;
  } else {
    return restScale;
  }
};

export const HeartRateIndicator = ({
  heartRate: newHeartRate = 0,
  color: colorOverride,
  size = 12,
  children,
  fillOpacity = 1,
}: HeartRateIndicatorProps) => {
  /**
   * Memo heart rate if it's within 10 BPM of the last value
   * to not disrupt the animation
   */
  const lastHeartRateRef = useRef(+newHeartRate);
  const heartRate = useMemo(() => {
    if (Math.abs(newHeartRate - lastHeartRateRef.current) <= 10) {
      return lastHeartRateRef.current;
    }
    lastHeartRateRef.current = newHeartRate;
    return newHeartRate;
  }, [newHeartRate]);

  const theme = useSystemTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const color = colorOverride || theme.accent;

  useEffect(() => {
    if (!heartRate || !containerRef.current) return;

    let animationId: number;

    const animate = () => {
      if (containerRef.current) {
        const scale = getHeartBeatScale(Date.now(), heartRate);
        containerRef.current.style.transform = `scale(${scale})`;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [heartRate]);

  if (!heartRate) {
    return (
      <HeartThinIcon
        fillOpacity={1}
        color={color}
        opacity={1}
        width={size}
        height={size}
        style={{
          maskImage: 'linear-gradient(to bottom, black, rgba(0, 0, 0, 0.5))',
          filter: 'grayscale(1)',
        }}
      />
    );
  }

  return (
    <div ref={containerRef}>
      {children ? (
        children
      ) : (
        <div style={{ fontSize: 0 }}>
          <HeartThinIcon
            fillOpacity={fillOpacity}
            color={color}
            width={size}
            height={size}
            style={{
              maskImage:
                'linear-gradient(to bottom, black, rgba(0, 0, 0, 0.8))',
            }}
          />
        </div>
      )}
    </div>
  );
};
